<template>
  <div class="mt-6">
    <div v-if="archive" class="lg:flex lg:justify-between mb-8">
      <div v-if="results.data.aggregations.categories && results.data.aggregations.categories.buckets">
        <label class="flex font-light text-dark">Filter by Category</label>

        <div class="flex items-center justify-start flex-wrap gap-2 mt-2">
          <label v-for="category in results.data.aggregations.categories.buckets" :key="category" class="flex cursor-pointer">
            <input
                type="checkbox"
                :value="category.key"
                v-model="filters.categories"
                class="hidden"
            />

            <span
                :class="['flex items-center justify-center gap-2 px-4 py-2 rounded-full font-medium text-dark transition-colors', category.selected ? 'bg-default' : 'bg-light']">
                  <span v-html="category.key" class="relative top-0.5"></span>

                  <svg width="24" height="25" viewBox="0 0 24 25"
                       :class="['fill-dark transition-transform', category.selected ? 'rotate-45' : 'rotate-0']"
                       fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                          d="M12.9231 5.42308V11.5769H19.0769C19.5769 11.5769 20 12 20 12.5C20 13.0385 19.5769 13.4231 19.0769 13.4231H12.9231V19.5769C12.9231 20.1154 12.5 20.5 12 20.5C11.4615 20.5 11.0769 20.1154 11.0769 19.5769V13.4231H4.92308C4.38462 13.4231 4 13.0385 4 12.5C4 12 4.38462 11.5769 4.92308 11.5769H11.0769V5.42308C11.0769 4.92308 11.4615 4.5 12 4.5C12.5 4.5 12.9231 4.92308 12.9231 5.42308Z"/>
                  </svg>
              </span>
          </label>
        </div>
      </div>

      <div class="flex items-end pt-4 lg:pt-0">
        <div class="flex items-center gap-4">
          <span class="w-14 text-base text-dark">Sort By</span>

          <div class="relative z-30">
            <a
                class="relative flex justify-between items-center w-full pl-5 rounded-full bg-light text-base text-grey"
                href="#"
                @click.prevent="showOrder = !showOrder"
            >
                  <span class="inline-flex pr-4">
                      <span v-if="true">--{{ orderName }}--</span>
                  </span>

              <span
                  class="flex justify-center items-center w-11 h-11 rounded-full border border-solid border-white transition-colors"
                  :class="showOrder ? 'bg-default' : 'bg-dark'"
              >
                      <svg class="transition-transform" width="16" height="10" viewBox="0 0 16 10" fill="none"
                           xmlns="http://www.w3.org/2000/svg"
                           :class="showOrder ? 'fill-dark translate-y-0.5' : 'fill-white translate-y-0'"
                      >
                          <path
                              d="M7.19732 9.22748L0.347826 2.37798C-0.115942 1.94989 -0.115942 1.20072 0.347826 0.772631C0.77592 0.308863 1.52508 0.308863 1.95318 0.772631L8.01784 6.80162L14.0468 0.772631C14.4749 0.308863 15.2241 0.308863 15.6522 0.772631C16.1159 1.20072 16.1159 1.94989 15.6522 2.37798L8.80268 9.22748C8.37458 9.69125 7.62542 9.69125 7.19732 9.22748Z"/>
                      </svg>
                  </span>
            </a>

            <ul v-if="showOrder"
                class="absolute left-0 top-full w-full mt-0.5 rounded-3xl shadow-md text-base bg-white overflow-hidden">
              <li v-for="(option, key) in orders" :key="key">
                <a
                    @click.prevent="sort(key)"
                    class="flex py-2 px-6 text-grey hover:text-dark hover:bg-primary cursor-pointer"
                >
                  {{ option.text }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 md:auto-rows-fr gap-6">
      <template v-for="(item, index) in results.data.items" :key="item.id">
        <card-post
            :item="item"
            :component="component"
        >
        </card-post>

        <div v-if="!newsletterLast && index === 3"
             class="relative flex justify-center items-center border-dark border-2 rounded-3xl p-4 overflow-hidden">
          <div class="text-center">
            <h4 class="heading-underline mb-4 text-xl lg:text-2xl bg-bottom">Subscribe to our Newsletter</h4>

            <p class="mb-4 text-xl font-light text-dark">We'll send you the latest news, events and offers.</p>

            <div>
              <slot name="newsletter"></slot>
            </div>
          </div>
        </div>
      </template>

      <div v-if="newsletterLast"
           class="relative flex justify-center items-center border-dark border-2 rounded-3xl p-4 overflow-hidden">
        <div class="text-center">
          <h4 class="heading-underline mb-4 text-xl lg:text-2xl bg-bottom">Subscribe to our Newsletter</h4>

          <p class="mb-4 text-xl font-light text-dark">We'll send you the latest news, events and offers.</p>

          <div class="relative z-20">
            <slot name="newsletter"></slot>
          </div>
        </div>
      </div>

      <div v-if="results.pagination.per_page < results.pagination.total " class="flex justify-center items-center">
        <button @click="loadMore" class="button button-default" data-track="Button Load More Articles">Load More Articles</button>
      </div>
    </div>
  </div>
</template>

<script>
import {defineComponent} from "vue"
import CardPost from "./CardPost.vue"
import itemsjs from "../plugins/itemsjs";
import dayjs from "dayjs";
import FiltersMixin from "./../mixins/filters.js"
import OrderMixin from "./../mixins/order.js"
import NavigationMixin from "./../mixins/navigation.js"

export default defineComponent({
  components: {
    CardPost,
  },
  mixins: [FiltersMixin, OrderMixin, NavigationMixin],
  props: {
    name: {
      type: String,
    },
    component: {
      type: String,
      default: "",
    },
    archive: {
      type: Boolean,
      default: false,
    },
    newsletterLast: {
      type: Boolean,
      default: false,
    },
    query: {
      type: String,
      default: "",
    }
  },
  data() {

    let aggregations = {}

    aggregations.categories = {
      title:  "Categories",
      conjunction: false,
      size: 100,
      sort: "key",
    }

    return {
      date: null,
      items: window?.capes?.[this.name].items ?? [],
      total: window?.capes?.[this.name]?.total ?? 0,
      page: window?.capes?.[this.name]?.page ?? 1,
      per_page: window?.capes?.[this.name].perPage === -1 || window?.capes?.[this.name].perPage === "-1" ? 10 : window?.capes?.[this.name].perPage,

      s: "",
      itemjs: {},
      filters: {},

      configuration: {
        //searchableFields: ['tags', 'categories'],
        sortings: {
          relevance: {
            field: "timestamp",
            order: "desc",
          },
        },
        aggregations,
      },
    }
  },
  computed: {
    results() {
      return this.itemsjs
          ? this.itemsjs.search({
            query: this.query,
            sort: this.orders[this.order],
            filters: this.filters,
            page: this.page,
            per_page: this.per_page,
            date: this.date,
            prefilter(items) {
              return items.filter((item) => {
                return item.dates.includes(dayjs(this.date).format("YYYY-MM-DD"))
              })
            },
          })
          : []
    },
  },
  methods: {
    loadMore() {
      this.per_page += 6
    },
  },
  created() {
    this.clearFilters()
    this.buildDistance()
    this.buildSorting()
    this.bindNavigation()

    this.itemsjs = itemsjs(this.items, this.configuration)

    this.watchFilters()
  },
})
</script>